import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _class, _GroupOfConditionsEditor;
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbBooleanLogicTreeContext } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTreeContext";
import { DRAG_DROP_TRANSFER_KEY } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTreeDragDrop";
import { AbBooleanLogicTreeFocus } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTreeFocus";
import { CompoundConditionIndexUtils } from "abreact/incubator/ui/abBooleanLogicTree/CompoundConditionIndexUtils";
import { GroupColumnOptionsMenu } from "abreact/incubator/ui/abBooleanLogicTree/GroupColumnOptionsMenu";
import { LogicTreeDropAffordance } from "abreact/incubator/ui/abBooleanLogicTree/LogicTreeDropAffordance";
import { AbBooleanLogicGroupingContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicGroupingContent";
import { AbBooleanLogicSingleConditionContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicSingleConditionContent";
import { AbBooleanLogicTreeSelectionMachine, SelectionMachineAction } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicTreeSelectionMachine";
import { SingleConditionRow } from "abreact/incubator/ui/abBooleanLogicTree/SingleConditionRow";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { LocaleUtils } from "abweb/utils/LocaleUtils";
import { List } from "immutable";
import { observer } from "mobx-react";
import * as React from "react";
// import SourceTarget = AbBooleanLogicTreeFocus.SourceTarget;
// import RowTarget = AbBooleanLogicTreeFocus.RowTarget;

export let GroupOfConditionsEditor = observer(_class = (_GroupOfConditionsEditor = class GroupOfConditionsEditor extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onAddAnotherClicked", () => {
      // console.log(`onAddAnotherClicked`); //`
      const newKey = this.props.keyGenerator();
      const newCondition = new AbBooleanLogicSingleConditionContent(newKey);
      const newState = this.props.groupEditorState.withAppendSingleRow(newCondition);
      this.onConditionAdded(newKey, newState);
    });
    _defineProperty(this, "onDeleteClicked", (index, conditionKey) => {
      /* The 'delete' action is only enabled when there are > 1 condition rows in the Conditional Assignment context.
       * Other implementations of the 'UGE' editor do not necessarily have this guardrail.
       * Add some sequence length checks to cover these unregulated cases. */
      let targetKey = undefined;
      const sequenceSize = this.props.groupEditorState.sequence.size;
      {
        if (sequenceSize === 1 && this.props.onDeleteGroup)
          // Only item in sequence
          return this.props.onDeleteGroup();
      }
      if (index === sequenceSize - 1) {
        // Last item in sequence
        const item = this.props.groupEditorState.sequence.get(index - 1);
        targetKey = this.getClosestSingleCondition(item, true).key;
      } else {
        // Regular item in sequence
        const item = this.props.groupEditorState.sequence.get(index + 1);
        targetKey = this.getClosestSingleCondition(item, false).key;
      }
      const immSequence = this.props.groupEditorState.sequence.delete(index);
      const newGroupState = this.props.groupEditorState.withSequence(immSequence);
      this.onChange(newGroupState);
      this.context.focusManager.setFocusRequest(new AbBooleanLogicTreeFocus.RowTarget(targetKey));
    });
    _defineProperty(this, "onConnectiveChangeAction", value => {
      const newGroupState = this.props.groupEditorState.withConnective(value);
      this.onChange(newGroupState);
    });
    _defineProperty(this, "onMakeSubGroupAction", index => {
      // indent the indexed condition by putting it into a subgroup
      // add an additional row to this subgroup, because the new subgroup
      // makes more sense if it starts with two rows instead of one
      let newGroupState = this.props.groupEditorState.withItemSubGrouped(index, this.props.keyGenerator());
      let newSubGroup = newGroupState.getItem(index);
      const newKey = this.props.keyGenerator();
      newSubGroup = newSubGroup.withAppendSingleRow(new AbBooleanLogicSingleConditionContent(newKey));
      newGroupState = newGroupState.withSequence(newGroupState.sequence.set(index, newSubGroup));
      this.context.focusManager.setFocusRequest(new AbBooleanLogicTreeFocus.SourceTarget(newKey));
      this.onChange(newGroupState, {
        newRowKeys: [newKey],
        newGroupKeys: [newSubGroup.key]
      });
    });
    _defineProperty(this, "onConditionAdded", (key, newState) => {
      this.context.focusManager.setFocusRequest(new AbBooleanLogicTreeFocus.SourceTarget(key));
      this.onChange(newState, {
        newRowKeys: [key]
      });
    });
    _defineProperty(this, "onInsertAbove", index => {
      const newKey = this.props.keyGenerator();
      let newState = this.props.groupEditorState.withItemInsertedAt(index, new AbBooleanLogicSingleConditionContent(newKey));
      this.onConditionAdded(newKey, newState);
    });
    _defineProperty(this, "onInsertBelow", index => {
      const newKey = this.props.keyGenerator();
      const newState = this.props.groupEditorState.withItemInsertedAt(index + 1, new AbBooleanLogicSingleConditionContent(newKey));
      this.onConditionAdded(newKey, newState);
    });
    _defineProperty(this, "onMoveUp", index => {
      const newState = this.props.groupEditorState.withItemMoveUp(index);
      this.onChange(newState);
    });
    _defineProperty(this, "onMoveDown", index => {
      const newState = this.props.groupEditorState.withItemMoveDown(index);
      this.onChange(newState);
    });
    _defineProperty(this, "onDuplicate", index => {
      const {
        groupEditorState,
        keyGenerator
      } = this.props;
      const row = groupEditorState.getItem(index);
      const newRowKey = keyGenerator();
      const newState = groupEditorState.withDuplicateCondition(index, newRowKey);
      this.context.focusManager.setFocusRequest(new AbBooleanLogicTreeFocus.SourceTarget(newRowKey));
      this.onChange(newState, {
        duplicatedRowKey: row.key,
        newRowKeys: [newRowKey]
      });
    });
    _defineProperty(this, "onGroupWithNext", index => {
      const {
        groupEditorState,
        keyGenerator
      } = this.props;
      if (groupEditorState.getItemCount() >= index + 2) {
        const rows = [groupEditorState.getItem(index), groupEditorState.getItem(index + 1)].filter(row => row);
        const newGroupKey = keyGenerator();
        let newGroup = new AbBooleanLogicGroupingContent(newGroupKey, groupEditorState.getOppositeConnective());
        rows.forEach(row => {
          if (ClassUtils.isInstanceOf(row, AbBooleanLogicSingleConditionContent)) {
            newGroup = newGroup.withAppendSingleRow(row);
          } else {
            newGroup = newGroup.withAppendSubgroup(row);
          }
        });
        const newState = groupEditorState.withItemRemovedAt(index).withItemRemovedAt(index).withSubgroupInsertedAt(index, newGroup);
        this.onChange(newState, {
          newGroupKeys: [newGroupKey]
        });
      }
    });
    _defineProperty(this, "onUngroupClicked", (index, subGroup) => {
      const arr = subGroup.sequence.toArray(); // need an array which splice can take with ...spread
      const immSequence = List(this.props.groupEditorState.sequence.splice(index, 1, ...arr));
      const newGroupState = this.props.groupEditorState.withSequence(immSequence);
      this.onChange(newGroupState);
    });
    _defineProperty(this, "onSubGroupChange", (index, newSubGroup, additionalInfo) => {
      // console.log(`:onSubGroupChange`, index, newSubGroup); //`
      const immSequence = newSubGroup.sequence.size === 0 ? this.props.groupEditorState.sequence.remove(index) : this.props.groupEditorState.sequence.set(index, newSubGroup);
      const newGroupState = this.props.groupEditorState.withSequence(immSequence);
      this.props.onChange(newGroupState, additionalInfo);
    });
    _defineProperty(this, "onDisableAllConditions", item => {
      const conditionAction = condition => {
        return condition.withDisabled(true);
      };
      const updatedGroupContent = item.performConditionalActionOnRows(conditionAction);
      this.onChange(updatedGroupContent);
    });
    _defineProperty(this, "onEnableAllConditions", item => {
      const conditionAction = condition => {
        return condition.withDisabled(false);
      };
      const updatedGroupContent = item.performConditionalActionOnRows(conditionAction);
      this.onChange(updatedGroupContent);
    });
    _defineProperty(this, "onDisableAllConditionsInGroup", (item, index) => {
      const conditionAction = condition => {
        return condition.withDisabled(true);
      };
      const updatedGroupContent = item.performConditionalActionOnRows(conditionAction);
      const newCurrentGroupState = this.props.groupEditorState.withGroupItemSetAt(index, updatedGroupContent);
      this.onChange(newCurrentGroupState);
    });
    _defineProperty(this, "onEnableAllConditionsInGroup", (item, index) => {
      const conditionAction = condition => {
        return condition.withDisabled(false);
      };
      const updatedGroupContent = item.performConditionalActionOnRows(conditionAction);
      const newCurrentGroupState = this.props.groupEditorState.withGroupItemSetAt(index, updatedGroupContent);
      this.onChange(newCurrentGroupState);
    });
    _defineProperty(this, "onDisableGroup", (item, index) => {
      const updatedGroupContent = item.withDisabled(true);
      const newCurrentGroupState = this.props.groupEditorState.withGroupItemSetAt(index, updatedGroupContent);
      this.onChange(newCurrentGroupState);
    });
    _defineProperty(this, "onEnableGroup", (item, index) => {
      const updatedGroupContent = item.withDisabled(false);
      const newCurrentGroupState = this.props.groupEditorState.withGroupItemSetAt(index, updatedGroupContent);
      this.onChange(newCurrentGroupState);
    });
    _defineProperty(this, "onDisableSingle", (singleConditionContent, index) => {
      let newSingleContent = singleConditionContent.withDisabled(true);
      const newCurrentGroupState = this.props.groupEditorState.withSingleItemSetAt(index, newSingleContent);
      this.onChange(newCurrentGroupState);
    });
    _defineProperty(this, "onEnableSingle", (singleConditionContent, index) => {
      let newSingleContent = singleConditionContent.withDisabled(false);
      const newCurrentGroupState = this.props.groupEditorState.withSingleItemSetAt(index, newSingleContent);
      this.onChange(newCurrentGroupState);
    });
    this.state = {
      isYAxisEmphasized: false,
      isRowBeingDragged: false,
      hoverRowIndex: -1,
      draggableHeaderIndex: null
    };
    this.setHoverRowIndex = this.setHoverRowIndex.bind(this);
    this.setDraggableHeaderIndex = this.setDraggableHeaderIndex.bind(this);
    this.onRowDragStart = this.onRowDragStart.bind(this);
    this.onRowDragEnd = this.onRowDragEnd.bind(this);
    this.onMakeSubGroupAction = this.onMakeSubGroupAction.bind(this);
    this.onDeleteClicked = this.onDeleteClicked.bind(this);
    this.onInsertAbove = this.onInsertAbove.bind(this);
    this.onInsertBelow = this.onInsertBelow.bind(this);
    this.onMoveUp = this.onMoveUp.bind(this);
    this.onMoveDown = this.onMoveDown.bind(this);
    this.onDuplicate = this.onDuplicate.bind(this);
    this.onGroupWithNext = this.onGroupWithNext.bind(this);
    this.onEnableSingle = this.onEnableSingle.bind(this);
    this.onDisableSingle = this.onDisableSingle.bind(this);
    this.onEnableGroup = this.onEnableGroup.bind(this);
    this.onDisableGroup = this.onDisableGroup.bind(this);
    this.maybeGenerateDataTestId = this.maybeGenerateDataTestId.bind(this);
    this.onDisableAllConditionsInGroup = this.onDisableAllConditionsInGroup.bind(this);
    this.onEnableAllConditionsInGroup = this.onEnableAllConditionsInGroup.bind(this);
  }
  isOutermostGroup() {
    return this.props.isRecursiveCall !== true;
  }
  getGroupRowCount() {
    return this.props.groupEditorState.sequence.count();
  }
  isReadOnly() {
    return this.props.readonly;
  }
  renderDropAffordance(classname, associatedKey) {
    if (this.isReadOnly() || this.props.isGroupDisabled || this.props.nestedUnderDisabled || associatedKey === undefined) {
      return /*#__PURE__*/React.createElement("div", {
        className: "ab-non-functional-drop-zone"
      });
    }
    return /*#__PURE__*/React.createElement(LogicTreeDropAffordance, {
      className: classname,
      associatedKey: associatedKey
    });
  }
  setHoverRowIndex(index) {
    this.setState({
      hoverRowIndex: index
    });
  }
  setDraggableHeaderIndex(index) {
    this.setState({
      draggableHeaderIndex: index
    });
  }
  maybeGenerateDataTestId(prefix) {
    const {
      dataTestIdGenerator,
      groupEditorState
    } = this.props;
    if (dataTestIdGenerator != null) {
      return dataTestIdGenerator.generateID(groupEditorState.key, prefix);
    }
    return prefix;
  }
  renderSingleConditionRow(item, index, conditionEvaluationResult) {
    const compoundIndex = CompoundConditionIndexUtils.next(this.props.compoundConditionGroupIndex, index);
    return /*#__PURE__*/React.createElement("li", {
      "aria-label": AbReactResources.abBooleanLogicTree_accessibility_singleConditionLabel(compoundIndex)
    }, /*#__PURE__*/React.createElement(SingleConditionRow, _extends({}, this.props, {
      index: index,
      item: item,
      nestedUnderDisabled: this.props.isGroupDisabled || this.props.nestedUnderDisabled,
      isConditionDisabled: item.disabled,
      compoundConditionRowIndex: compoundIndex,
      lastItemIndex: this.getGroupRowCount() - 1,
      conditionKey: item.key,
      hoverRow: this.state.hoverRowIndex === index,
      setHoverRowIndex: this.setHoverRowIndex,
      draggable: this.state.draggableHeaderIndex === index,
      supportsSubgroups: this.props.supportsSubgroups,
      setDraggableHeaderIndex: this.setDraggableHeaderIndex,
      onRowDragStart: this.onRowDragStart,
      onRowDragEnd: this.onRowDragEnd,
      conditionEvaluationResult: conditionEvaluationResult,
      onMakeSubGroupAction: this.onMakeSubGroupAction,
      onDeleteClicked: this.onDeleteClicked,
      onInsertAbove: this.onInsertAbove,
      onInsertBelow: this.onInsertBelow,
      onMoveUp: this.onMoveUp,
      onMoveDown: this.onMoveDown,
      onDuplicate: this.onDuplicate,
      onGroupWithNext: this.onGroupWithNext,
      onEnableSingle: this.onEnableSingle,
      onDisableSingle: this.onDisableSingle,
      selectionMachine: this.props.selectionMachine
    })));
  }
  isSingleConditionOutermostGroup() {
    if (this.isOutermostGroup()) {
      const count = this.getGroupRowCount();
      return count < 2 && (count === 0 || this.props.groupEditorState.getItem(0).cardinality === "SINGLE");
    }
    return false;
  }
  renderGroupHeader() {
    if (this.isSingleConditionOutermostGroup()) {
      return null;
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "ab-group-of-conditions-editor-header"
    }, this.renderBooleanOperator());
  }
  renderBooleanOperator() {
    const className = CssUtils.composeClassNames("ab-booltree-boolean-operator", (this.isReadOnly() || this.props.isGroupDisabled || this.props.nestedUnderDisabled) && "disabled-or-readonly");
    return this.props.supportsSubgroups !== false ? /*#__PURE__*/React.createElement(GroupColumnOptionsMenu, {
      className: className,
      readonly: this.isReadOnly() || this.props.isGroupDisabled || this.props.nestedUnderDisabled,
      value: this.props.groupEditorState.connective,
      onChange: value => this.onConnectiveChangeAction(value),
      onEmphasisChanged: emphasis => {
        this.setState({
          isYAxisEmphasized: emphasis
        });
      },
      isOutermostGroup: this.isOutermostGroup(),
      onMoveUp: this.props.onMoveUp,
      onMoveDown: this.props.onMoveDown,
      onInsertAbove: this.props.onInsertAbove,
      onInsertBelow: this.props.onInsertBelow,
      onDeleteGroup: this.props.onDeleteGroup,
      onUnfoldGroup: this.props.onUnfoldGroup,
      isFullyDisabled: this.isGroupFullyDisabled,
      isFullyEnabled: this.isGroupFullyEnabled,
      onDisableAllConditions: () => this.onDisableAllConditions(this.props.groupEditorState),
      onEnableAllConditions: () => this.onEnableAllConditions(this.props.groupEditorState),
      onClearAll: this.props.onClearAll,
      isFirstItem: false,
      isLastItem: false,
      serverSupportsDisabledConditionSection: this.props.serverSupportsDisabledConditionSection,
      dataTestId: this.maybeGenerateDataTestId()
    }) : null;
  }
  get isGroupFullyDisabled() {
    let foundEnabledCondition = false;
    const conditionAction = rowOrGroup => {
      if (ClassUtils.isInstanceOf(rowOrGroup, AbBooleanLogicSingleConditionContent) && !rowOrGroup.disabled) {
        foundEnabledCondition = true;
      }
    };
    this.props.groupEditorState.visitAllNodes(conditionAction);
    return !foundEnabledCondition;
  }
  get isGroupFullyEnabled() {
    let foundDisabledCondition = false;
    const conditionAction = rowOrGroup => {
      if (ClassUtils.isInstanceOf(rowOrGroup, AbBooleanLogicSingleConditionContent) && rowOrGroup.disabled) {
        foundDisabledCondition = true;
      }
    };
    this.props.groupEditorState.visitAllNodes(conditionAction);
    return !foundDisabledCondition;
  }

  // A ConditionSection can/could be disabled using the model flag or with previou versions of the UI, we must still provide the option
  // to enable a condition group if it has been disabled at the group level.
  renderEnableSubgroup() {
    if (!this.isOutermostGroup() && !this.props.readonly && this.props.isGroupDisabled && !this.props.nestedUnderDisabled) {
      return /*#__PURE__*/React.createElement("div", {
        className: "ab-guided-expression-editor-subgroup-enable-button"
      }, /*#__PURE__*/React.createElement("a", {
        onClick: () => this.props.onEnableGroup?.(),
        "data-test-id": "enable-condition-subgroup"
      }, LocaleUtils.localizeMe("Enable Subgroup")));
    }
  }
  renderGroupRow(item, index) {
    const itemKey = item.key;
    const isReadOnly = this.isReadOnly();
    const className = CssUtils.composeClassNames("ab-guided-expression-editor-group-wrapper", item.disabled && "disabled-condition-group");
    const compoundConditionRowIndex = CompoundConditionIndexUtils.next(this.props.compoundConditionGroupIndex, index);
    const dataTestId = `subgroup-${compoundConditionRowIndex}`;
    return /*#__PURE__*/React.createElement("li", {
      "aria-label": AbReactResources.abBooleanLogicTree_accessibility_conditionGroupLabel(compoundConditionRowIndex),
      className: className,
      key: itemKey,
      "data-test-id": dataTestId
    }, /*#__PURE__*/React.createElement(GroupOfConditionsEditor, {
      key: itemKey + "-group-editor",
      compoundConditionGroupIndex: compoundConditionRowIndex,
      groupEditorState: item,
      onChange: (groupEditorState, additionalInfo) => this.onSubGroupChange(index, groupEditorState, additionalInfo),
      readonly: isReadOnly,
      customConditionComponent: this.props.customConditionComponent,
      onUngroupClicked: () => this.onUngroupClicked(index, item),
      keyGenerator: this.props.keyGenerator,
      onBeforeUndoableChange: this.props.onBeforeUndoableChange,
      onAfterUndoableChange: this.props.onAfterUndoableChange,
      isRecursiveCall: true,
      onMoveUp: () => this.onMoveUp(index),
      onMoveDown: () => this.onMoveDown(index),
      onInsertAbove: () => this.onInsertAbove(index),
      onInsertBelow: () => this.onInsertBelow(index),
      onUnfoldGroup: () => this.onUngroupClicked(index, item),
      onDeleteGroup: () => this.onDeleteClicked(index, item.key),
      conditionEvaluator: this.props.conditionEvaluator,
      level: this.props.level + 1,
      dragDropMachine: this.props.dragDropMachine,
      selectionMachine: this.props.selectionMachine,
      dataTestIdGenerator: this.props.dataTestIdGenerator,
      onDisableGroup: () => this.onDisableGroup(item, index),
      onEnableGroup: () => this.onEnableGroup(item, index),
      onDisableAllConditionsInGroup: () => this.onDisableAllConditionsInGroup(item, index),
      onEnableAllConditionsInGroup: () => this.onEnableAllConditionsInGroup(item, index),
      nestedUnderDisabled: this.props.isGroupDisabled || this.props.nestedUnderDisabled,
      isGroupDisabled: item.disabled,
      serverSupportsDisabledConditionSection: this.props.serverSupportsDisabledConditionSection,
      rowLanguageSource: this.props.rowLanguageSource
    }));
  }
  onDragOver(e) {
    const {
      dragDropMachine
    } = this.props;
    if (dragDropMachine.isValidDropCandidate(e)) {
      e.preventDefault(); // Prevent Auto-Rendered mouse disable icon
      this.props.dragDropMachine.onDragOver(e);
    } else {
      // If we are over an invalid target, we should clear current drop target
      this.props.dragDropMachine.clearCurrentDropTarget();
    }
  }
  onDrop(e) {
    const conditionKey = this.props.dragDropMachine.getDragConditionKey(e);
    if (conditionKey) {
      const {
        onDropCondition
      } = this.props;
      onDropCondition && onDropCondition(conditionKey, e.dataTransfer);
    }
  }
  renderSingleRow(item, key) {
    if (item == null) {
      return null;
    }
    let row;
    const index = key;
    if (item.cardinality === "SINGLE") {
      row = this.renderSingleConditionRow(item, index, this.computeConditionEvaluationResult(item, index));
    } else if (item.cardinality === "GROUP") {
      row = this.renderGroupRow(item, index);
    } else {
      return null;
    }
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: item.key + "-fragment"
    }, row, this.renderDropAffordance(item.key + "-drop-affordance", item.key));
  }

  /*
   * Figure out whether to display true, false, N/A, or nothing.
   */
  computeConditionEvaluationResult(item, index) {
    const {
      conditionEvaluator
    } = this.props;
    if (conditionEvaluator != null) {
      const compoundConditionRowIndex = CompoundConditionIndexUtils.next(this.props.compoundConditionGroupIndex, index);
      const key = {
        itemKey: item.key,
        compoundRowIndexKey: `condition-${compoundConditionRowIndex}`
      };
      return conditionEvaluator(key) ?? "N/A";
    }
  }
  render() {
    const {
      readonly: isReadOnly
    } = this.props;
    const isOutermostGroup = this.isOutermostGroup();
    const onDragOver = isOutermostGroup ? this.onDragOver.bind(this) : undefined;
    const onDrop = isOutermostGroup ? this.onDrop.bind(this) : undefined;
    const firstConditionKey = this.props.groupEditorState.sequence.first()?.key;
    const className = CssUtils.composeClassNames("ab-group-of-conditions-editor", isReadOnly && "ab-group-of-conditions-editor-readonly");
    const contentClassName = CssUtils.composeClassNames("ab-group-of-conditions-editor-content", isReadOnly && "ab-booltree-group-readonly", isOutermostGroup && "ab-outermost-group", this.isSingleConditionOutermostGroup() && "single-condition-outer-group");
    const addRowLabel = this.props.rowLanguageSource.addRowLabel;
    const addSubgroupLabel = AbReactResources.abBooleanLogicTree_addSubgroup();
    return /*#__PURE__*/React.createElement("div", {
      className: className,
      onDragOver: onDragOver,
      onDrop: onDrop,
      "data-test-id": this.maybeGenerateDataTestId("group")
    }, this.renderGroupHeader(), this.renderEnableSubgroup(), /*#__PURE__*/React.createElement("ol", {
      className: contentClassName,
      "aria-label": AbReactResources.abBooleanLogicTree_accessibility_conditionGroupLabel(this.props.groupEditorState.connective)
    }, this.renderDropAffordance("header-row-drop-affordance", firstConditionKey), this.props.groupEditorState.sequence.map((item, key) => this.renderSingleRow(item, key)), !isReadOnly && !this.props.groupEditorState.disabled && !this.props.nestedUnderDisabled && /*#__PURE__*/React.createElement("div", {
      className: "ab-group-of-conditions-editor-footer"
    }, /*#__PURE__*/React.createElement("a", {
      tabIndex: 0,
      onKeyDown: this.onAddConditionKeyDown.bind(this),
      className: "abui ab-booltree-add-button",
      onClick: this.onAddAnotherClicked,
      "data-test-id": this.maybeGenerateDataTestId("add-btn")
    }, addRowLabel), this.props.supportsSubgroups !== false && /*#__PURE__*/React.createElement("a", {
      tabIndex: 0,
      onKeyDown: this.onAddSubgroupKeyDown.bind(this),
      className: "abui ab-booltree-add-button",
      onClick: () => this.onAddSubgroup(),
      "data-test-id": this.maybeGenerateDataTestId("add-subgroup-btn")
    }, addSubgroupLabel))));
  }
  onAddConditionKeyDown(event) {
    if (event.key === KeyValue.ENTER) {
      this.onAddAnotherClicked();
    }
  }
  onAddSubgroupKeyDown(event) {
    if (event.key === KeyValue.ENTER) {
      this.onAddSubgroup();
    }
  }
  onRowDragStart(e, conditionKey) {
    if (this.props.selectionMachine !== undefined) {
      /** A drag requires a click, and a click indicates a selection action, so, we want to select the condition before
       *  performing the drag, to ensure behavior matches expectations. **/
      if (!this.props.selectionMachine.isSelected(conditionKey)) this.props.selectionMachine.performSelectionOnConditionClicked(conditionKey, e);

      /** ActionTypeEnum.DRAG will set the dataTransfer Data to contain the selected conditions **/
      this.props.selectionMachine.performSelectionAction({
        actionType: SelectionMachineAction.ActionTypeEnum.DRAG,
        dataTransfer: e.dataTransfer,
        selectionMachine: this.props.selectionMachine
      });
      /** Create and use a transient HTML Canvas element for display in the drag-action **/
      const displayElem = AbBooleanLogicTreeSelectionMachine.getDragDisplayElement(this.props.selectionMachine);
      e.dataTransfer.setDragImage(displayElem, -5, 0);
    }
    this.props.dragDropMachine.startDrag(conditionKey);
    this.setState({
      isRowBeingDragged: true
    });
    e.dataTransfer.setData(DRAG_DROP_TRANSFER_KEY, conditionKey);
    e.dataTransfer.effectAllowed = "move";
  }
  onRowDragEnd() {
    this.props.dragDropMachine.endDrag();
    this.setState({
      isRowBeingDragged: false
    });
  }
  onChange(newState, additionalInfo) {
    const {
      onBeforeUndoableChange,
      onChange,
      onAfterUndoableChange
    } = this.props;
    onBeforeUndoableChange();
    onChange(newState, additionalInfo);
    onAfterUndoableChange();
  }
  onAddSubgroup() {
    const {
      keyGenerator,
      groupEditorState
    } = this.props;
    const newKey1 = keyGenerator();
    const newKey2 = keyGenerator();
    const condition1 = new AbBooleanLogicSingleConditionContent(newKey1);
    const condition2 = new AbBooleanLogicSingleConditionContent(newKey2);
    const groupKey = keyGenerator();
    const newGroup = new AbBooleanLogicGroupingContent(groupKey, groupEditorState.getOppositeConnective()).withAppendSingleRow(condition1).withAppendSingleRow(condition2);
    const newState = groupEditorState.withAppendSubgroup(newGroup);
    this.context.focusManager.setFocusRequest(new AbBooleanLogicTreeFocus.SourceTarget(newKey1));
    this.onChange(newState, {
      newRowKeys: [newKey1, newKey2],
      newGroupKeys: [groupKey]
    });
  }
  /* getClosestSingleCondition: Will find the closest single-condition and return it. 'above' will indicate if
   * it will return the closest condition above, or if it will return the closest below.  */
  getClosestSingleCondition(item, above) {
    if (item.cardinality === "SINGLE") {
      return item;
    } else {
      if (above) {
        return this.getClosestSingleCondition(item.sequence.get(item.sequence.size - 1), above);
      }
      return this.getClosestSingleCondition(item.sequence.get(0), above);
    }
  }
}, _defineProperty(_GroupOfConditionsEditor, "contextType", AbBooleanLogicTreeContext), _defineProperty(_GroupOfConditionsEditor, "defaultProps", {
  level: 1
}), _GroupOfConditionsEditor)) || _class;
//# sourceMappingURL=GroupOfConditionsEditor.js.map
